import { Typography } from '@mui/material'
import { FormikHelpers, useFormikContext } from 'formik'
import { isNil } from 'lodash'
import { ReactNode, useMemo, useState } from 'react'
import { RxPlus } from 'react-icons/rx'
import { BreadcrumbObjectType, DataType } from '~/common/generated/apollo-operations'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { DossIcon } from '~/components/DossIcon'
import { WorkspaceIcon } from '~/components/navigation/WorkspaceIcon'
import { SettingsPageLookupGroup } from '../../tables/types'
import { Dropdown } from '../../ui/molecules/dropdown/Dropdown'
import { ColumnFormValues } from '../types'
import { LookupTableAndFieldDropdown } from './LookupTableAndFieldDropdown'
interface LookupColumnFieldsProps {
  column?: ColumnFormValues | null
  lookupColumnGroups: SettingsPageLookupGroup[]
  isArray: boolean
  columnHasNonNullCells: boolean
  onSubmit?: (
    values: ColumnFormValues,
    {
      setSubmitting,
      resetForm,
    }: Pick<FormikHelpers<ColumnFormValues>, 'setSubmitting' | 'resetForm'>,
  ) => void
  tableId: string
}

enum LookupGroupOptionType {
  EXISTING = 'EXISTING',
  NEW = 'NEW',
}

type LookupTypeDropdownOption =
  | {
      type: LookupGroupOptionType.EXISTING
      lookupColumnGroupId: string
      lookupTableId: string
      label: string
      icon: ReactNode
    }
  | { type: LookupGroupOptionType.NEW; label: string; icon: ReactNode }

/**
 * Renders the fields for creating or updating a lookup type column
 */
export function LookupColumnFields({
  lookupColumnGroups,
  isArray,
  columnHasNonNullCells,
  onSubmit,
  tableId,
}: LookupColumnFieldsProps) {
  const { t } = useTypedTranslation()
  const formik = useFormikContext<ColumnFormValues>()
  // If the value in the lookup column group dropdown changes, auto focus on the next field
  // to make the dropdown for the next field to open automatically if there is not already
  // a selected lookup column group and the field is not disabled
  const [isDropdownOpen, setIsDropdownOpen] = useState(
    !columnHasNonNullCells && isNil(formik.values.lookupColumnGroupId),
  )
  const [isTableAndFieldDropdownOpen, setIsTableAndFieldDropdownOpen] = useState(false)

  const lookupColumnGroupOptions: LookupTypeDropdownOption[] = useMemo(() => {
    // If is array, filter out any lookup column groups that have any non-array column
    // If is not an array, filter out any lookup column groups that have any array column
    const columnGroupOptions = lookupColumnGroups
      .filter((group) => {
        if (group.columns.length === 0) {
          return true
        }
        if (isArray) {
          return !group.columns.some((column) => column.type !== DataType.ARRAY)
        }

        return !group.columns.some((column) => column.type === DataType.ARRAY)
      })

      .map((columnGroup) => {
        const lookupTable = columnGroup.lookupTable
        const columns = columnGroup.columns

        const label = `${lookupTable.name}: ${columns.map((column) => column.name).join(', ')}`
        return {
          type: LookupGroupOptionType.EXISTING,
          lookupColumnGroupId: columnGroup.id,
          lookupTableId: columnGroup.lookupTable.id,
          label,
          icon: columnGroup.lookupTable.icon ? (
            <DossIcon name={columnGroup.lookupTable.icon.name} size={16} />
          ) : (
            <WorkspaceIcon workspaceType={BreadcrumbObjectType.TABLE} />
          ),
        }
      })

    const addNewLookupOption: LookupTypeDropdownOption = {
      type: LookupGroupOptionType.NEW,
      label: t('table_column.new_lookup'),
      icon: <RxPlus />,
    }
    return [...columnGroupOptions, addNewLookupOption]
  }, [lookupColumnGroups, isArray, t])

  const selectedLookupGroupOption = useMemo(() => {
    if (formik.values.shouldCreateNewLookupGroup) {
      return lookupColumnGroupOptions.find((option) => option.type === LookupGroupOptionType.NEW)
    }
    if (formik.values.lookupColumnGroupId) {
      return lookupColumnGroupOptions.find(
        (option) =>
          option.type === LookupGroupOptionType.EXISTING &&
          option.lookupColumnGroupId === formik.values.lookupColumnGroupId,
      )
    }
    return
  }, [
    formik.values.shouldCreateNewLookupGroup,
    formik.values.lookupColumnGroupId,
    lookupColumnGroupOptions,
  ])

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1 px-1">
        <Typography className="py-2 text-gray-9" variant="body1">
          {t('table.create_column.group')}
        </Typography>
        <div>
          <Dropdown
            autoFocus={isDropdownOpen}
            disableClearable
            disabled={columnHasNonNullCells}
            getOptionDisplay={(option: LookupTypeDropdownOption) => {
              return (
                <div className="flex items-center gap-2">
                  <div className="shrink-0">{option.icon}</div>
                  {option.label}
                </div>
              )
            }}
            getOptionLabel={(option: LookupTypeDropdownOption) => {
              return option.label
            }}
            isOptionEqualToValue={(option: LookupTypeDropdownOption) => {
              if (option.type === LookupGroupOptionType.NEW) {
                return formik.values.shouldCreateNewLookupGroup
              } else {
                return formik.values.lookupColumnGroupId === option.lookupColumnGroupId
              }
            }}
            onChange={(_, newValue) => {
              if (newValue.type === LookupGroupOptionType.NEW) {
                formik.setFieldValue('lookupColumnGroupId', null, false)
                formik.setFieldValue('shouldCreateNewLookupGroup', true, false)
              } else {
                formik.setFieldValue('lookupColumnGroupId', newValue.lookupColumnGroupId, false)
                formik.setFieldValue('shouldCreateNewLookupGroup', false, false)
              }
              formik.setFieldValue('lookupTableId', null, false)
              // Setting should validate to true when updating lookup column id to null to
              // disable the save button
              formik.setFieldValue('lookupColumnId', null, true)
              setIsTableAndFieldDropdownOpen(true)
            }}
            onClose={() => setIsDropdownOpen(false)}
            onOpen={() => setIsDropdownOpen(true)}
            open={isDropdownOpen}
            options={lookupColumnGroupOptions}
            showCheckmark={false}
            startAdornment={
              selectedLookupGroupOption ? (
                <div className="me-[-8px]">{selectedLookupGroupOption.icon}</div>
              ) : undefined
            }
            value={selectedLookupGroupOption}
            variant="default"
          />
        </div>
      </div>
      {(formik.values.lookupColumnGroupId || formik.values.shouldCreateNewLookupGroup) && (
        <LookupTableAndFieldDropdown
          columnHasNonNullCells={columnHasNonNullCells}
          isDropdownOpen={isTableAndFieldDropdownOpen}
          lookupColumnGroups={lookupColumnGroups}
          onSave={onSubmit}
          setIsDropdownOpen={setIsTableAndFieldDropdownOpen}
          tableId={tableId}
        />
      )}
    </div>
  )
}
